import { useCallback, useEffect, useState } from "react";
import { BellOutlined } from "@ant-design/icons";
import { Badge, Empty, Popover, Spin } from "antd";
import moment from "moment";
import DataProvider from "../../apis";

interface NotificationItem {
  id: number;
  title: string;
  content: string;
  isRead: boolean;
  createdAt: string;
}

// `getNotifications`/`markNotificationAsRead` already existed in
// apis/index.ts but had zero consumers anywhere in the app — this is the
// first real UI wired to them. Fetch-all + mark-in-place (not
// fetch-unread-only + remove-on-click) deliberately mirrors
// new-doctor-app's app/notifications.tsx: opening a notification there
// just clears its unread dot and it stays in the list — it doesn't vanish.
// This used to fetch unread-only and drop an item the instant it was
// clicked, which read as "notifications disappear when you view them".
export default function NotificationsBell() {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    DataProvider.getNotifications(true)
      .then((res) => {
        const data = res.data;
        setItems(Array.isArray(data) ? data : []);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    // Light polling so the unread badge doesn't go stale across a long
    // session without needing a websocket/push channel for something this
    // low-stakes.
    const timer = setInterval(load, 60_000);
    return () => clearInterval(timer);
  }, [load]);

  const unreadCount = items.filter((n) => !n.isRead).length;

  const markRead = (id: number) => {
    const target = items.find((n) => n.id === id);
    if (!target || target.isRead) return;
    // Optimistic — flip the flag in place rather than removing the item, so
    // it stays visible with just its unread dot cleared (matches
    // new-doctor-app). Worst case on failure, the dot silently comes back
    // on the next poll — an acceptable trade for not blocking the click.
    setItems((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    );
    DataProvider.markNotificationAsRead(id).catch(() => {
      load();
    });
  };

  // There's no bulk "mark all as read" endpoint on the backend — only
  // PATCH .../messages/:id/read for one at a time — so this fires that same
  // call for every currently-unread item. Unlike the single-item version,
  // this one does wait for the calls to settle before reloading: with N
  // requests in flight, a partial failure is much more likely than with
  // one, so it's worth reconciling against the server afterward rather than
  // trusting the optimistic update.
  const markAllRead = () => {
    const unreadIds = items.filter((n) => !n.isRead).map((n) => n.id);
    if (unreadIds.length === 0 || markingAll) return;
    setMarkingAll(true);
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    Promise.allSettled(
      unreadIds.map((id) => DataProvider.markNotificationAsRead(id))
    )
      .then((results) => {
        if (results.some((r) => r.status === "rejected")) load();
      })
      .finally(() => setMarkingAll(false));
  };

  const content = (
    <div className="w-80">
      {loading && items.length === 0 ? (
        <div className="flex justify-center py-8">
          <Spin size="small" />
        </div>
      ) : items.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No notifications yet"
          className="py-6"
        />
      ) : (
        <ul className="m-0 max-h-96 list-none space-y-1 overflow-y-auto p-0">
          {items.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => markRead(n.id)}
                className="flex w-full flex-col items-start gap-0.5 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-slate-50"
              >
                <span className="flex w-full items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-slate-800">
                    {n.title}
                  </span>
                  {!n.isRead && (
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#00A2F8]" />
                  )}
                </span>
                <span className="line-clamp-2 text-xs text-slate-500">
                  {n.content}
                </span>
                <span className="mt-0.5 text-[11px] text-slate-400">
                  {moment(n.createdAt).fromNow()}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      title={
        <div className="flex items-center justify-between gap-3">
          <span className="font-semibold text-slate-800">Notifications</span>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              disabled={markingAll}
              className="text-xs font-medium text-[#0070C0] transition-colors hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {markingAll ? "Marking…" : "Mark all as read"}
            </button>
          )}
        </div>
      }
      content={content}
    >
      <button
        type="button"
        className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
        aria-label="Notifications"
      >
        <Badge count={unreadCount} size="small" offset={[-2, 2]}>
          <BellOutlined className="text-lg" />
        </Badge>
      </button>
    </Popover>
  );
}
